import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Clock, Calendar, Bell, Settings, Globe, ListOrdered } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: "monday", label: "Monday" },
  { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" },
  { key: "thursday", label: "Thursday" },
  { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
  { key: "sunday", label: "Sunday" }
];

// Common timezones for the dropdown
const TIMEZONES = [
  { value: "UTC", label: "UTC (Coordinated Universal Time)" },
  { value: "America/New_York", label: "Eastern Time (US & Canada)" },
  { value: "America/Chicago", label: "Central Time (US & Canada)" },
  { value: "America/Denver", label: "Mountain Time (US & Canada)" },
  { value: "America/Los_Angeles", label: "Pacific Time (US & Canada)" },
  { value: "America/Toronto", label: "Toronto (Canada)" },
  { value: "America/Vancouver", label: "Vancouver (Canada)" },
  { value: "America/Argentina/Buenos_Aires", label: "Buenos Aires (Argentina)" },
  { value: "America/Sao_Paulo", label: "São Paulo (Brazil)" },
  { value: "America/Mexico_City", label: "Mexico City (Mexico)" },
  { value: "America/Bogota", label: "Bogotá (Colombia)" },
  { value: "America/Lima", label: "Lima (Peru)" },
  { value: "America/Santiago", label: "Santiago (Chile)" },
  { value: "Europe/London", label: "London (UK)" },
  { value: "Europe/Paris", label: "Paris (France)" },
  { value: "Europe/Berlin", label: "Berlin (Germany)" },
  { value: "Europe/Madrid", label: "Madrid (Spain)" },
  { value: "Europe/Rome", label: "Rome (Italy)" },
  { value: "Europe/Amsterdam", label: "Amsterdam (Netherlands)" },
  { value: "Europe/Brussels", label: "Brussels (Belgium)" },
  { value: "Europe/Zurich", label: "Zurich (Switzerland)" },
  { value: "Europe/Stockholm", label: "Stockholm (Sweden)" },
  { value: "Europe/Moscow", label: "Moscow (Russia)" },
  { value: "Asia/Dubai", label: "Dubai (UAE)" },
  { value: "Asia/Kolkata", label: "India (IST)" },
  { value: "Asia/Singapore", label: "Singapore" },
  { value: "Asia/Hong_Kong", label: "Hong Kong" },
  { value: "Asia/Shanghai", label: "Shanghai (China)" },
  { value: "Asia/Tokyo", label: "Tokyo (Japan)" },
  { value: "Asia/Seoul", label: "Seoul (South Korea)" },
  { value: "Asia/Bangkok", label: "Bangkok (Thailand)" },
  { value: "Asia/Jakarta", label: "Jakarta (Indonesia)" },
  { value: "Australia/Sydney", label: "Sydney (Australia)" },
  { value: "Australia/Melbourne", label: "Melbourne (Australia)" },
  { value: "Pacific/Auckland", label: "Auckland (New Zealand)" },
  { value: "Africa/Cairo", label: "Cairo (Egypt)" },
  { value: "Africa/Johannesburg", label: "Johannesburg (South Africa)" },
  { value: "Africa/Lagos", label: "Lagos (Nigeria)" }
];

const DEFAULT_SETTINGS = {
  enabled: true,
  default_duration_minutes: 30,
  buffer_between_appointments: 15,
  max_slots_to_show: 10,
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  working_hours: {
    monday: { enabled: true, start: "09:00", end: "17:00" },
    tuesday: { enabled: true, start: "09:00", end: "17:00" },
    wednesday: { enabled: true, start: "09:00", end: "17:00" },
    thursday: { enabled: true, start: "09:00", end: "17:00" },
    friday: { enabled: true, start: "09:00", end: "17:00" },
    saturday: { enabled: false, start: "10:00", end: "14:00" },
    sunday: { enabled: false, start: "10:00", end: "14:00" }
  },
  min_notice_hours: 2,
  max_advance_days: 30
};

export default function AppointmentSettings({ actorId }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (actorId) {
      fetchSettings();
    }
  }, [actorId]);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${BACKEND_URL}/api/appointments/settings/${actorId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      }
    } catch (error) {
      // If 404, use defaults
      if (error.response?.status !== 404) {
        console.error("Failed to fetch settings:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${BACKEND_URL}/api/appointments/settings/${actorId}`,
        settings,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Settings saved successfully!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateWorkingHours = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: {
          ...prev.working_hours[day],
          [field]: value
        }
      }
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 flex justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* General Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            General Settings
          </CardTitle>
          <CardDescription>
            Configure how appointments are booked and managed
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-base">Enable Appointment Booking</Label>
              <p className="text-sm text-slate-500">Allow users to book appointments through AI chat</p>
            </div>
            <Switch
              checked={settings.enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, enabled: checked })}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Duration */}
            <div className="space-y-2">
              <Label htmlFor="duration" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Default Appointment Duration
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="duration"
                  type="number"
                  value={settings.default_duration_minutes}
                  onChange={(e) => setSettings({ ...settings, default_duration_minutes: parseInt(e.target.value) || 30 })}
                  min={15}
                  max={240}
                  className="w-24"
                />
                <span className="text-slate-500">minutes</span>
              </div>
            </div>

            {/* Buffer */}
            <div className="space-y-2">
              <Label htmlFor="buffer" className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Buffer Between Appointments
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="buffer"
                  type="number"
                  value={settings.buffer_between_appointments}
                  onChange={(e) => setSettings({ ...settings, buffer_between_appointments: parseInt(e.target.value) || 0 })}
                  min={0}
                  max={60}
                  className="w-24"
                />
                <span className="text-slate-500">minutes</span>
              </div>
            </div>

            {/* Min Notice */}
            <div className="space-y-2">
              <Label htmlFor="notice" className="flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Minimum Notice Required
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="notice"
                  type="number"
                  value={settings.min_notice_hours}
                  onChange={(e) => setSettings({ ...settings, min_notice_hours: parseInt(e.target.value) || 1 })}
                  min={0}
                  max={72}
                  className="w-24"
                />
                <span className="text-slate-500">hours</span>
              </div>
              <p className="text-xs text-slate-400">How far in advance appointments must be booked</p>
            </div>

            {/* Max Advance */}
            <div className="space-y-2">
              <Label htmlFor="advance" className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                Maximum Advance Booking
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="advance"
                  type="number"
                  value={settings.max_advance_days}
                  onChange={(e) => setSettings({ ...settings, max_advance_days: parseInt(e.target.value) || 30 })}
                  min={1}
                  max={365}
                  className="w-24"
                />
                <span className="text-slate-500">days</span>
              </div>
              <p className="text-xs text-slate-400">How far in the future appointments can be booked</p>
            </div>

            {/* Max Slots to Show */}
            <div className="space-y-2">
              <Label htmlFor="maxSlots" className="flex items-center gap-2">
                <ListOrdered className="w-4 h-4" />
                Maximum Slots to Display
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="maxSlots"
                  type="number"
                  value={settings.max_slots_to_show || 10}
                  onChange={(e) => setSettings({ ...settings, max_slots_to_show: Math.min(10, Math.max(1, parseInt(e.target.value) || 10)) })}
                  min={1}
                  max={10}
                  className="w-24"
                />
                <span className="text-slate-500">slots</span>
              </div>
              <p className="text-xs text-slate-400">Number of available time slots shown during booking (max 10)</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Working Hours */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Working Hours
              </CardTitle>
              <CardDescription>
                Set your available hours for each day of the week
              </CardDescription>
            </div>
            {/* Timezone Display */}
            <div className="flex items-center gap-2 text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg">
              <Globe className="w-4 h-4" />
              <span className="font-medium">{settings.timezone || "UTC"}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Timezone Selector */}
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <Label className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4" />
              Working Hours Timezone
            </Label>
            <Select
              value={settings.timezone || "UTC"}
              onValueChange={(value) => setSettings({ ...settings, timezone: value })}
            >
              <SelectTrigger className="w-full md:w-96">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {TIMEZONES.map((tz) => (
                  <SelectItem key={tz.value} value={tz.value}>
                    {tz.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 mt-2">
              All working hours below are in this timezone. Available slots will be shown to users in their own timezone.
            </p>
          </div>

          <div className="space-y-4">
            {DAYS_OF_WEEK.map(({ key, label }) => (
              <div 
                key={key} 
                className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                  settings.working_hours[key]?.enabled 
                    ? 'bg-slate-50' 
                    : 'bg-slate-100 opacity-60'
                }`}
              >
                <Switch
                  checked={settings.working_hours[key]?.enabled ?? false}
                  onCheckedChange={(checked) => updateWorkingHours(key, "enabled", checked)}
                />
                <span className="w-28 font-medium text-slate-700">{label}</span>
                
                {settings.working_hours[key]?.enabled && (
                  <>
                    <Input
                      type="time"
                      value={settings.working_hours[key]?.start || "09:00"}
                      onChange={(e) => updateWorkingHours(key, "start", e.target.value)}
                      className="w-32"
                    />
                    <span className="text-slate-400">to</span>
                    <Input
                      type="time"
                      value={settings.working_hours[key]?.end || "17:00"}
                      onChange={(e) => updateWorkingHours(key, "end", e.target.value)}
                      className="w-32"
                    />
                  </>
                )}
                
                {!settings.working_hours[key]?.enabled && (
                  <span className="text-slate-400 italic">Closed</span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveSettings} disabled={saving} className="flex items-center gap-2">
          {saving ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
          ) : (
            <Save className="w-4 h-4" />
          )}
          Save Settings
        </Button>
      </div>
    </div>
  );
}
